import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usersApi } from '../../services/adminApi';

const UsersList = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await usersApi.getAll();
      if (response.data.success) {
        const usersData = Array.isArray(response.data.data) 
          ? response.data.data 
          : response.data.data?.data || [];
        setUsers(usersData);
      } else {
        setUsers([]);
      }
    } catch (err) {
      console.error('Users fetch error:', err);
      setError('Kullanıcılar yüklenirken hata oluştu');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Bu kullanıcıyı silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      await usersApi.delete(id);
      await fetchUsers();
    } catch (err) {
      console.error('Delete error:', err);
      alert('Silme işlemi sırasında hata oluştu');
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin': return '👑';
      case 'user': return '👤';
      default: return '❓';
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'admin': return 'Yönetici';
      case 'user': return 'Kullanıcı';
      default: return 'Bilinmiyor';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const UserRow = ({ user, onDelete }) => {
    const tableRowStyle = {
      backgroundColor: 'white',
      borderBottom: '1px solid #e5e7eb',
      transition: 'all 0.2s'
    };

    const tableCellStyle = {
      padding: '1rem',
      fontSize: '0.875rem',
      color: '#374151'
    };

    const statusBadgeStyle = {
      padding: '0.25rem 0.75rem',
      borderRadius: '9999px',
      fontSize: '0.75rem',
      fontWeight: '500',
      backgroundColor: user.is_active ? '#dcfce7' : '#fee2e2',
      color: user.is_active ? '#166534' : '#dc2626'
    };

    const roleBadgeStyle = {
      padding: '0.25rem 0.75rem',
      borderRadius: '9999px',
      fontSize: '0.75rem',
      fontWeight: '500',
      backgroundColor: user.role === 'admin' ? '#dbeafe' : '#f3f4f6',
      color: user.role === 'admin' ? '#1e40af' : '#374151'
    };

    const actionButtonStyle = {
      padding: '0.5rem',
      borderRadius: '0.375rem',
      border: 'none',
      fontSize: '0.75rem',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.2s',
      marginRight: '0.5rem'
    };

    return (
      <tr style={tableRowStyle}>
        <td style={tableCellStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{
              width: '2.5rem',
              height: '2.5rem',
              borderRadius: '50%',
              backgroundColor: '#f3f4f6',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1rem',
              color: '#6b7280'
            }}>
              {getRoleIcon(user.role)}
            </div>
            <div>
              <div style={{ fontWeight: '600', color: '#111827', marginBottom: '0.25rem' }}>
                {user.name}
              </div>
              <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                {user.email}
              </div>
            </div>
          </div>
        </td>
        <td style={tableCellStyle}>
          <span style={roleBadgeStyle}>
            {getRoleLabel(user.role)}
          </span>
        </td>
        <td style={tableCellStyle}>
          <span style={statusBadgeStyle}>
            {user.is_active ? 'Aktif' : 'Pasif'}
          </span>
        </td>
        <td style={tableCellStyle}>
          <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
            {formatDate(user.last_login_at)}
          </div>
        </td>
        <td style={tableCellStyle}>
          <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
            {formatDate(user.created_at)}
          </div>
        </td>
        <td style={tableCellStyle}>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              onClick={() => navigate(`/admin/users/${user.id}/edit`)}
              style={{
                ...actionButtonStyle,
                backgroundColor: '#3b82f6',
                color: 'white'
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = '#2563eb'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#3b82f6'}
            >
              Düzenle
            </button>
            <button
              onClick={() => onDelete(user.id)}
              style={{
                ...actionButtonStyle,
                backgroundColor: '#dc2626',
                color: 'white'
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = '#b91c1c'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#dc2626'}
            >
              Sil
            </button>
          </div>
        </td>
      </tr>
    );
  };

  const headerCardStyle = {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    borderRadius: '0.75rem',
    padding: '1.5rem',
    marginBottom: '1.5rem',
    color: 'white',
    boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
  };

  const addButtonStyle = {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    color: 'white',
    padding: '0.75rem 1.5rem',
    borderRadius: '0.5rem',
    border: '1px solid rgba(255, 255, 255, 0.3)',
    fontSize: '0.875rem',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s',
    textDecoration: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.5rem'
  };

  const tableStyle = {
    width: '100%',
    backgroundColor: 'white',
    borderRadius: '0.75rem',
    overflow: 'hidden',
    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)'
  };

  const tableHeaderStyle = {
    backgroundColor: '#f8fafc',
    padding: '1rem',
    fontSize: '0.75rem',
    fontWeight: '600',
    color: '#374151',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    borderBottom: '1px solid #e5e7eb'
  };

  const loadingStyle = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '3rem',
    fontSize: '1rem',
    color: '#6b7280'
  };

  const errorStyle = {
    backgroundColor: '#fee2e2',
    border: '1px solid #fecaca',
    borderRadius: '0.5rem',
    padding: '1rem',
    marginBottom: '1rem',
    color: '#dc2626',
    fontSize: '0.875rem'
  };

  const emptyStateStyle = {
    textAlign: 'center',
    padding: '3rem',
    color: '#6b7280'
  };

  if (loading) {
    return (
      <div style={loadingStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <div style={{
            width: '1rem',
            height: '1rem',
            border: '2px solid #e5e7eb',
            borderTop: '2px solid #3b82f6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          Kullanıcılar yükleniyor...
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '1.5rem' }}>
      <div style={headerCardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1 style={{ margin: '0 0 0.5rem 0', fontSize: '1.875rem', fontWeight: '700' }}>
              Kullanıcılar
            </h1>
            <p style={{ margin: 0, opacity: 0.9 }}>
              Sistem kullanıcılarını yönetin
            </p>
          </div>
          <button
            onClick={() => navigate('/admin/users/create')}
            style={addButtonStyle}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.3)';
              e.target.style.transform = 'translateY(-1px)';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
              e.target.style.transform = 'translateY(0)';
            }}
          >
            ➕ Yeni Kullanıcı
          </button>
        </div>
      </div>

      {error && (
        <div style={errorStyle}>
          ⚠️ {error}
        </div>
      )}

      <div style={tableStyle}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ ...tableHeaderStyle, textAlign: 'left' }}>Kullanıcı</th>
              <th style={{ ...tableHeaderStyle, textAlign: 'center', width: '120px' }}>Rol</th>
              <th style={{ ...tableHeaderStyle, textAlign: 'center', width: '100px' }}>Durum</th>
              <th style={{ ...tableHeaderStyle, textAlign: 'center', width: '150px' }}>Son Giriş</th>
              <th style={{ ...tableHeaderStyle, textAlign: 'center', width: '150px' }}>Oluşturulma</th>
              <th style={{ ...tableHeaderStyle, textAlign: 'center', width: '200px' }}>İşlemler</th>
            </tr>
          </thead>
          <tbody>
            {!users || users.length === 0 ? (
              <tr>
                <td colSpan="6" style={emptyStateStyle}>
                  <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>👥</div>
                  <div style={{ fontSize: '1.125rem', fontWeight: '500', marginBottom: '0.5rem' }}>
                    Henüz kullanıcı yok
                  </div>
                  <div style={{ fontSize: '0.875rem', color: '#9ca3af' }}>
                    İlk kullanıcınızı eklemek için yukarıdaki butona tıklayın
                  </div>
                </td>
              </tr>
            ) : (
              users && users.length > 0 ? users.map(user => (
                <UserRow
                  key={user.id}
                  user={user}
                  onDelete={handleDelete}
                />
              )) : null
            )}
          </tbody>
        </table>
      </div>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default UsersList;
